import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Input, Alert } from "antd";
import {
  resetPassword,
  validateTokenResetPassword,
} from "../../../services/authService";
import ImageWithBasePath from "../../../components/Common/imageWithBasePath";
import { useTranslation } from "react-i18next";
import { all_routes } from "../../../router/all_routes";

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

  useEffect(() => {
    const checkToken = async () => {
      try {
        const response = await validateTokenResetPassword(token || undefined);
        setTokenValid(response.data.isValid);
      } catch (err) {
        setTokenValid(false);
      }
    };

    if (token) checkToken();
  }, [token]);

  const handleSubmit = async (values: {
    password: string;
    confirm: string;
  }) => {
    setLoading(true);
    setError(null);
    try {
      if (!token) {
        throw new Error("token_invalid");
      }
      await resetPassword(token, values.password);
      setSuccess(true);
      setTimeout(() => navigate(all_routes.login), 2000);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div
        className="row justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="col-md-5">
          <div className="px-3 py-4">
            <div className="text-center mb-3">
              <ImageWithBasePath
                src="assets/img/authentication/logo-1.webp"
                className="mw-100"
                alt="logo"
              />
            </div>

            <div className="card">
              <div className="card-body p-4">
                <div className="mb-4 text-center">
                  <h2 className="mb-2">{t("reset_password")}</h2>
                  <p className="mb-0">{t("enter_new_password")}</p>
                </div>

                {error && (
                  <Alert message={error} type="error" className="mb-3" />
                )}
                {success && (
                  <Alert
                    message={t("password_reset_success")}
                    type="success"
                    className="mb-3"
                  />
                )}

                {tokenValid === false && (
                  <Alert
                    type="error"
                    showIcon
                    message={t("invalid_or_expired_link")}
                    description={
                      <div className="mt-2">
                        <button
                          className="btn btn-primary w-100"
                          onClick={() => navigate(all_routes.login)}
                        >
                          {t("back_to_login")}
                        </button>
                      </div>
                    }
                    className="mb-3"
                  />
                )}

                {tokenValid && !success && (
                  <Form layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                      name="password"
                      label={t("new_password")}
                      rules={[
                        { required: true, message: t("enter_password") },
                        {
                          validator(_, value) {
                            if (!value || strongPasswordRegex.test(value)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(t("password_policy"))
                            );
                          },
                        },
                      ]}
                      hasFeedback
                    >
                      <Input.Password />
                    </Form.Item>

                    <Form.Item
                      name="confirm"
                      label={t("confirm_password")}
                      dependencies={["password"]}
                      rules={[
                        { required: true, message: t("confirm_your_password") },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(t("passwords_do_not_match"))
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password />
                    </Form.Item>

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={loading}
                    >
                      {t("reset_password")}
                    </button>
                  </Form>
                )}
              </div>
            </div>

            <div className="text-center mt-3">
              <p className="mb-0">
                {t("copyright")} © {new Date().getFullYear()} - Schoolation
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
